import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { apiEndpoints } from 'src/app/api-endpoints';
import { Store, select } from '@ngrx/store';
import { selectSelectedApplication } from 'src/app/store/app-state.selectors';
import { switchMap, take } from 'rxjs/operators';
import { UserTable } from './models/user-table.model';

@Injectable({
  providedIn: 'root'
})
export class UserTableService {
  private apiUrl = environment.apiConfig.baseUri + apiEndpoints.userTable;
  private userTableApis = {
    createTable: '/create-table',
    updateTable: '/update-table',
    deleteTable: '/delete-table',
    getTablesForApplication: '/get-tables-for-application',
  };

  constructor(
    private http: HttpClient,
    private store: Store
  ) { }

  getUserTables(): Observable<UserTable[]> {
    return this.store.pipe(
      select(selectSelectedApplication),
      take(1),
      switchMap(applicationId => {
        if (!applicationId) {
          throw new Error('No application selected');
        }
        
        const request = { applicationId };
        return this.http.post<{ tables: UserTable[] }>(
          `${this.apiUrl}${this.userTableApis.getTablesForApplication}`,
          request
        );
      }),
      switchMap(response => of(response.tables || []))
    );
  }

  createUserTable(table: Omit<UserTable, 'tableId' | 'createdDate' | 'modifiedDate'>): Observable<UserTable> {
    return this.store.pipe(
      select(selectSelectedApplication),
      take(1),
      switchMap(applicationId => {
        if (!applicationId) {
          throw new Error('No application selected');
        }
        
        const request = {
          applicationId,
          tableName: table.tableName,
          tableDescription: table.tableDescription,
          isShared: table.isShared,
          isDefault: table.isDefault,
          sharedTo: table.sharedTo || [],
          createdBy: table.createdBy,
          modifiedBy: table.modifiedBy,
          columnList: table.columnList
        };
        
        return this.http.post<UserTable>(
          `${this.apiUrl}${this.userTableApis.createTable}`,
          request
        );
      })
    );
  }

  updateUserTable(tableId: string, table: Partial<UserTable>): Observable<UserTable> {
    return this.store.pipe(
      select(selectSelectedApplication),
      take(1),
      switchMap(applicationId => {
        if (!applicationId) {
          throw new Error('No application selected');
        }
        
        const request = {
          applicationId,
          tableId,
          tableName: table.tableName,
          tableDescription: table.tableDescription,
          isShared: table.isShared,
          isDefault: table.isDefault,
          sharedTo: table.sharedTo,
          modifiedBy: table.modifiedBy,
          columnList: table.columnList
        };
        
        return this.http.post<UserTable>(
          `${this.apiUrl}${this.userTableApis.updateTable}`,
          request
        );
      })
    );
  }

  deleteUserTable(tableId: string): Observable<boolean> {
    return this.store.pipe(
      select(selectSelectedApplication),
      take(1),
      switchMap(applicationId => {
        if (!applicationId) {
          throw new Error('No application selected');
        }
        
        const request = {
          applicationId,
          tableId
        };
        
        return this.http.post<boolean>(
          `${this.apiUrl}${this.userTableApis.deleteTable}`,
          request
        );
      }),
      switchMap(response => of(response))
    );
  }
}
